import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import StatisticsBarChartDiagram from "../../../../components/diagrams/StatisticsBarChartDiagram";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


const SUBJECTS = ["matematyki", "języka polskiego", "języka angielskiego", "biologii", "geografii", "historii"];
const GRADES = [1, 2, 3, 4, 5, 6];

const randInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
const pickOne = (arr) => arr[randInt(0, arr.length - 1)];

function getRaw(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }
  if (typeof value === "object") {
    if ("raw" in value && value.raw != null) return String(value.raw);
    if ("latex" in value && value.latex != null) return String(value.latex);
    if ("value" in value && value.value != null && typeof value.value !== "object") {
      return String(value.value);
    }
    if ("text" in value && value.text != null) return String(value.text);
    if ("expr" in value && value.expr != null) return String(value.expr);
  }
  return String(value);
}

function gcdInt(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function mixedToNumber(wholeStr, numStr, denStr) {
  const w = parseInt(wholeStr, 10);
  const n = parseInt(numStr, 10);
  const d = parseInt(denStr, 10);
  if (!d) return null;
  const sign = w < 0 ? -1 : 1;
  return w + sign * (Math.abs(n) / Math.abs(d));
}

function toNumber(value) {
  const raw0 = getRaw(value);
  if (raw0 == null) return null;
  const raw = raw0.trim();
  if (!raw) return null;

  const s = raw.replace(",", ".").replace(/\s+/g, " ");

  const mixedLatex = s.match(/^(-?\d+)\s*\\frac\{(-?\d+)\}\{(-?\d+)\}$/);
  if (mixedLatex) return mixedToNumber(mixedLatex[1], mixedLatex[2], mixedLatex[3]);

  const fracLatex = s.match(/^\\frac\{(-?\d+)\}\{(-?\d+)\}$/);
  if (fracLatex) {
    const num = parseFloat(fracLatex[1]);
    const den = parseFloat(fracLatex[2]);
    return den === 0 ? null : num / den;
  }

  const mixedAscii = s.match(/^(-?\d+)\s+(-?\d+)\/(-?\d+)$/);
  if (mixedAscii) return mixedToNumber(mixedAscii[1], mixedAscii[2], mixedAscii[3]);

  const fracAscii = s.match(/^(-?\d+)\/(-?\d+)$/);
  if (fracAscii) {
    const num = parseFloat(fracAscii[1]);
    const den = parseFloat(fracAscii[2]);
    return den === 0 ? null : num / den;
  }

  if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);
  return null;
}

function numbersEqual(a, b, eps = 1e-6) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) < eps;
}

function mixedFractionLatex(num, den) {
  const whole = Math.trunc(num / den);
  const rem = Math.abs(num % den);
  const absDen = Math.abs(den);
  if (rem === 0) return String(whole);
  if (whole === 0) return `\\frac{${rem}}{${absDen}}`;
  return `${whole}\\frac{${rem}}{${absDen}}`;
}

function formatUserLatex(value) {
  let raw = getRaw(value).trim();
  if (!raw) return "\\text{--}";
  if (raw.startsWith("\\")) return raw;

  raw = raw.replace(",", ".");
  const mixedAscii = raw.match(/^(-?\d+)\s+(-?\d+)\/(-?\d+)$/);
  if (mixedAscii) {
    return `${mixedAscii[1]}\\frac{${Math.abs(parseInt(mixedAscii[2], 10))}}{${Math.abs(parseInt(mixedAscii[3], 10))}}`;
  }
  const fracAscii = raw.match(/^(-?\d+)\/(-?\d+)$/);
  if (fracAscii) return `\\frac{${fracAscii[1]}}{${fracAscii[2]}}`;
  return raw;
}

function hasUniqueMode(counts) {
  const max = Math.max(...counts);
  return counts.filter((count) => count === max).length === 1;
}

function generateCounts(total) {
  const counts = new Array(GRADES.length).fill(1);
  let remaining = total - counts.length;

  // Delikatnie premiujemy wyższe oceny, żeby rozkład często przypominał maturę.
  const weights = [1, 2, 3, 4, 5, 6];
  const weightSum = weights.reduce((a, b) => a + b, 0);

  while (remaining > 0) {
    let r = Math.random() * weightSum;
    let idx = 0;
    while (idx < weights.length - 1 && r >= weights[idx]) {
      r -= weights[idx];
      idx += 1;
    }
    counts[idx] += 1;
    remaining -= 1;
  }

  return counts;
}

function expandValues(categories, counts) {
  const values = [];
  categories.forEach((grade, idx) => {
    for (let i = 0; i < counts[idx]; i += 1) values.push(grade);
  });
  return values.sort((a, b) => a - b);
}

function medianFromValues(values) {
  const n = values.length;
  if (n % 2 === 1) {
    return { num: values[(n - 1) / 2], den: 1 };
  }
  let num = values[n / 2 - 1] + values[n / 2];
  let den = 2;
  const g = gcdInt(num, den);
  num /= g;
  den /= g;
  return { num, den };
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 30:
 *   diagram ocen z matematyki (24 uczniów): 1,3,4,4,5,7.
 *   Mediana = (4+5)/2 = 4,5;  dominanta = 6.
 */
function createMay2025OriginalTask() {
  return {
    type: "MaturaGradesMedianModeOpenTask",
    data: {
      subject: "matematyki",
      categories: [1, 2, 3, 4, 5, 6],
      counts: [1, 3, 4, 4, 5, 7],
      total: 24,
      median: 4.5,
      medianNum: 9,
      medianDen: 2,
      mode: 6,
      maxPoints: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 30,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const subject = pickOne(SUBJECTS);
  let total = randInt(20, 36);
  let counts = null;

  for (let attempt = 0; attempt < 80; attempt += 1) {
    total = randInt(20, 36);
    const candidate = generateCounts(total);
    if (hasUniqueMode(candidate)) {
      counts = candidate;
      break;
    }
  }
  if (!counts) counts = generateCounts(total);

  const values = expandValues(GRADES, counts);
  const medianData = medianFromValues(values);
  const median = medianData.num / medianData.den;
  const maxCount = Math.max(...counts);
  const mode = GRADES[counts.indexOf(maxCount)];

  return {
    type: "MaturaGradesMedianModeOpenTask",
    data: {
      subject,
      categories: GRADES,
      counts,
      total,
      median,
      medianNum: medianData.num,
      medianDen: medianData.den,
      mode,
      maxPoints: 2,
    },
  };
}

export function renderTask({ data }) {
  const { subject, categories, counts, total } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - otwarte (0-2 pkt).</strong>
      </p>

      <p>
        Na diagramie przedstawiono wyniki sprawdzianu z {subject} w pewnej
        klasie maturalnej liczącej <InlineMath math={String(total)} /> uczniów.
        Na osi poziomej podano oceny, które uzyskali uczniowie tej klasy, a na
        osi pionowej podano liczbę uczniów, którzy otrzymali daną ocenę.
      </p>

      <StatisticsBarChartDiagram
        categories={categories}
        counts={counts}
        xLabel="ocena"
        yLabel="liczba uczniów"
      />

      <p>
        Uzupełnij zdania. Wpisz odpowiednie liczby w wykropkowanych miejscach,
        aby zdania były prawdziwe.
      </p>

      <ol className="list-decimal ml-6 space-y-1">
        <li>
          Mediana ocen uzyskanych z tego sprawdzianu przez uczniów tej klasy jest równa{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Dominanta ocen uzyskanych z tego sprawdzianu przez uczniów tej klasy jest równa{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
      </ol>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField = (key) => (val) => onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: "grid", rowGap: 16, maxWidth: 420 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: "grid", rowGap: 4 }}>
        <div>
          <strong>1.</strong> Mediana:
        </div>
        <FractionInput
          value={ua.d1}
          onChange={setField("d1")}
          disabled={disabled}
          placeholder="np. 4 albo 4 1/2"
          label=""
        />
      </div>

      <div style={{ display: "grid", rowGap: 4 }}>
        <div>
          <strong>2.</strong> Dominanta:
        </div>
        <FractionInput
          value={ua.d2}
          onChange={setField("d2")}
          disabled={disabled}
          placeholder="np. 6"
          label=""
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const medianOk = numbersEqual(toNumber(ua.d1), data.median);
  const modeOk = numbersEqual(toNumber(ua.d2), data.mode);
  const score = [medianOk, modeOk].filter(Boolean).length;

  return {
    isCorrect: score === 2,
    correct: score === 2,
    score,
    maxScore: 2,
    parts: {
      "1) mediana": medianOk,
      "2) dominanta": modeOk,
    },
  };
}

export function renderAnswer({ data }) {
  const medianLatex = mixedFractionLatex(data.medianNum, data.medianDen);
  return (
    <div style={{ marginTop: 8, display: "grid", rowGap: 4 }}>
      <div>
        <strong>1.</strong> <InlineMath math={medianLatex} />
      </div>
      <div>
        <strong>2.</strong> <InlineMath math={String(data.mode)} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const isEmpty = () => {
    if (!userAnswer) return true;
    return ["d1", "d2"].every((key) => {
      const value = userAnswer[key];
      if (value == null) return true;
      return getRaw(value).trim() === "";
    });
  };

  if (isEmpty()) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 8, display: "grid", rowGap: 4 }}>
      <div>
        <strong>1.</strong> <InlineMath math={formatUserLatex(userAnswer.d1)} />
      </div>
      <div>
        <strong>2.</strong> <InlineMath math={formatUserLatex(userAnswer.d2)} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { categories, counts, total, medianNum, medianDen, mode } = data;
  const medianLatex = mixedFractionLatex(medianNum, medianDen);
  const isOdd = total % 2 === 1;
  const pos1 = isOdd ? (total + 1) / 2 : total / 2;
  const pos2 = isOdd ? null : total / 2 + 1;

  let cumulative = 0;
  let medianVal1 = null;
  let medianVal2 = null;
  const ranges = categories.map((grade, idx) => {
    const from = cumulative + 1;
    cumulative += counts[idx];
    const to = cumulative;
    if (medianVal1 == null && pos1 >= from && pos1 <= to) medianVal1 = grade;
    if (pos2 != null && medianVal2 == null && pos2 >= from && pos2 <= to) {
      medianVal2 = grade;
    }
    return { grade, from, to };
  });

  const maxCount = Math.max(...counts);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Z diagramu odczytujemy liczebności ocen.
      </p>

      <ul className="list-disc ml-6">
        {categories.map((grade, idx) => (
          <li key={grade}>
            ocena <InlineMath math={String(grade)} /> - liczba uczniów:{" "}
            <InlineMath math={String(counts[idx])} />
          </li>
        ))}
      </ul>

      <p>
        Razem mamy <InlineMath math={String(total)} /> uczniów.
      </p>

      <p>
        <strong>Krok 2.</strong> Wyznaczamy medianę, czyli środkową wartość w
        uporządkowanym zestawie ocen.
      </p>

      <p>
        {isOdd ? (
          <>
            Ponieważ liczba ocen jest nieparzysta, mediana jest oceną na pozycji{" "}
            <InlineMath math={String(pos1)} />.
          </>
        ) : (
          <>
            Ponieważ liczba ocen jest parzysta, bierzemy oceny na pozycjach{" "}
            <InlineMath math={String(pos1)} /> i <InlineMath math={String(pos2)} />.
          </>
        )}
      </p>

      <p>Pozycje poszczególnych ocen w uporządkowanym zestawie:</p>
      <ul className="list-disc ml-6">
        {ranges.map(({ grade, from, to }) => (
          <li key={grade}>
            ocena <InlineMath math={String(grade)} /> zajmuje {from === to ? "pozycję" : "pozycje od"}{" "}
            <InlineMath math={String(from)} />{from === to ? null : <> do <InlineMath math={String(to)} /></>}
          </li>
        ))}
      </ul>

      {isOdd ? (
        <BlockMath math={`Me = ${medianLatex}`} />
      ) : (
        <BlockMath math={`Me = \\frac{${medianVal1}+${medianVal2}}{2} = ${medianLatex}`} />
      )}

      <p>
        <strong>Krok 3.</strong> Dominanta to ocena, która występuje najczęściej.
        Największa liczba uczniów to <InlineMath math={String(maxCount)} /> i
        odpowiada ona ocenie <InlineMath math={String(mode)} />.
      </p>

      <BlockMath math={`D = ${mode}`} />
    </div>
  );
}
